import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native';
import { SceneMap, TabBar, TabView } from 'react-native-tab-view';

import { useToast } from '@fhs/auth/src/hooks';
import {
  ActionSheet,
  ActionSheetScreenHeader,
  FadeView,
  IconStarRateFill,
  MediaQueriesContext,
  Skeleton,
  Text,
  mobileMediaQueries,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import {
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';

import { DisplayCard } from './display-card';
import { EarnTab } from './earn-tab';
import { InRestaurantRedemption } from './in-restaurant-redemption';
import { RedeemTab } from './redeem-tab';

const renderScene = SceneMap({
  first: EarnTab,
  second: RedeemTab,
});

const tabRoutes = [
  { key: 'first', title: 'Earn' },
  { key: 'second', title: 'Redeem' },
];

const renderTabBar = props => (
  <TabBar
    {...props}
    indicatorStyle={styles.tabBarIndicator}
    activeColor={tokens.colors.$houseRed}
    inactiveColor={tokens.colors.$houseDark}
    renderLabel={({ color, route }) => (
      <Text.Ui style={[styles.tabBarLabel, { color }]} size="lg" weight="semibold">
        {route.title}
      </Text.Ui>
    )}
    style={styles.tabBar}
  />
);

function ErrorToast() {
  const toast = useToast();
  const { shortCodeState, resetInRestaurantRedemption } = useInRestaurantRedemptionContext();
  const resetInRestaurantRedemptionRef = useBoxRef(resetInRestaurantRedemption);
  useEffect(() => {
    if (shortCodeState === ShortCodeState.RequestError) {
      resetInRestaurantRedemptionRef.current();
      setTimeout(() => {
        toast.show({
          text: "Couldn't retrieve your code.",
          variant: 'negative',
        });
      }, 100);
    }
  }, [shortCodeState, toast, resetInRestaurantRedemptionRef]);
  return null;
}

function DisplayCardHeader() {
  const { loyaltyUserReady, loyaltyUser } = useLoyaltyLegacyStates();

  return (
    <DisplayCard
      image={
        <View style={styles.iconContainer}>
          <IconStarRateFill size={24} color={tokens.colors.$houseYellow} />
        </View>
      }
      style={styles.displayCard}
      button={{
        label: 'Learn More',
        onPress: () => {
          router.dismiss();
          setTimeout(() => {
            router.navigate(routes.rewardsHistory);
          }, 100);
        },
      }}
    >
      {!loyaltyUserReady ? (
        <Skeleton style={styles.skeleton} />
      ) : (
        <>
          <Text.Ui size="xs">You've got:</Text.Ui>
          <Text.Ui size="md" weight="semibold">
            {loyaltyUser?.points?.toLocaleString()} points
          </Text.Ui>
        </>
      )}
    </DisplayCard>
  );
}

export function InStoreRedeemScreen() {
  const { tab: selectedTab } = useLocalSearchParams<{ tab?: 'earn' | 'redeem' }>();
  const [index, setIndex] = useState(selectedTab === 'redeem' ? 1 : 0);

  const { inStoreRewardTierId } = useInRestaurantRedemptionContext();

  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const containerStyle = isDesktop ? { height: screenHeight } : ({ height: '100%' } as const);
  const drawerProps = useMemo(() => ({ style: { flex: 1 } }), []);

  const onClose = useCallback(() => {
    router.dismiss();
  }, []);

  return (
    <ActionSheet
      onRequestClose={onClose}
      isVisible
      onClose={onClose}
      containerStyle={containerStyle}
      ignoreBottomSafeArea
      drawerProps={drawerProps}
    >
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <ActionSheetScreenHeader title="Scan In Restaurant" style={styles.header} />
        {inStoreRewardTierId ? (
          <FadeView style={{ flex: 1 }}>
            <InRestaurantRedemption />
          </FadeView>
        ) : (
          <>
            <DisplayCardHeader />
            <TabView
              navigationState={{ index, routes: tabRoutes }}
              renderScene={renderScene}
              renderTabBar={renderTabBar}
              onIndexChange={setIndex}
              initialLayout={{ width: screenWidth }}
              style={{ flex: 1 }}
            />
          </>
        )}
      </MediaQueriesContext.Provider>
      <ErrorToast />
    </ActionSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: 0,
  },
  displayCard: {
    marginBottom: 12,
    marginHorizontal: 12,
  },
  tabBar: {
    backgroundColor: tokens.colors.$white,
  },
  tabBarLabel: {
    paddingVertical: 8,
  },
  tabBarIndicator: {
    backgroundColor: tokens.colors.$houseRed,
  },
  iconContainer: {
    borderRadius: 100,
    backgroundColor: tokens.colors.$blackOpacity04,
    padding: 8,
  },
  skeleton: {
    width: '50%',
    height: 20,
  },
});
